import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Clock, History, Plus, Pencil, Trash2, Users } from "lucide-react";
import { useRecentProjectChanges, useGearAuditLog, GearAuditLogEntry } from "@/hooks/useGearAuditLog";
import { formatDistanceToNow, parseISO } from "date-fns";

interface GearEditStatusProps {
  projectId: string;
  isPublicView?: boolean;
}

export function GearEditStatus({ projectId, isPublicView = false }: GearEditStatusProps) {
  const [showHistory, setShowHistory] = useState(false);
  const { data: recentChanges } = useRecentProjectChanges(projectId, 5);
  const { data: auditLog, isLoading: isLoadingHistory } = useGearAuditLog(projectId);

  const getActionIcon = (action: string) => {
    switch (action) {
      case "insert":
        return <Plus className="h-3 w-3" />;
      case "update":
        return <Pencil className="h-3 w-3" />;
      case "delete":
        return <Trash2 className="h-3 w-3" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case "insert":
        return "text-green-600";
      case "update":
        return "text-blue-600";
      case "delete":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const formatFieldName = (fieldName: string | null) => {
    if (!fieldName) return "";
    
    switch (fieldName) {
      case "item_created":
        return "Item created";
      case "item_deleted":
        return "Item deleted";
      case "cost_per_unit":
        return "Cost per unit";
      case "days_needed":
        return "Days needed";
      default:
        return fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    }
  };

  const formatChangeDescription = (entry: GearAuditLogEntry) => {
    if (entry.action === "insert") {
      return `Created "${entry.item_name}"`;
    }
    
    if (entry.action === "delete") {
      return `Deleted "${entry.item_name}"`;
    }
    
    if (entry.action === "update" && entry.field_name) {
      const fieldName = formatFieldName(entry.field_name);
      const oldVal = entry.old_value || "empty";
      const newVal = entry.new_value || "empty";
      
      return `Changed ${fieldName.toLowerCase()} from "${oldVal}" to "${newVal}" for "${entry.item_name}"`;
    }
    
    return "Unknown change";
  };

  return (
    <div className="flex items-center gap-2">
      {!isPublicView && (
        <Dialog open={showHistory} onOpenChange={setShowHistory}>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground hover:text-foreground">
              <History className="h-3 w-3" />
              History
            </Button>
          </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Change History
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            {isLoadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : !auditLog || auditLog.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No changes recorded yet</p>
                <p className="text-sm">Start editing gear items to see the change history</p>
              </div>
            ) : (
              <div className="space-y-3">
                {auditLog.map((entry) => (
                  <Card key={entry.id} className="p-3">
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 ${getActionColor(entry.action)}`}>
                        {getActionIcon(entry.action)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {formatChangeDescription(entry)}
                        </p>
                        
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Users className="h-3 w-3" />
                          <span>{entry.changed_by_email || "Unknown user"}</span>
                          <span>•</span>
                          <Clock className="h-3 w-3" />
                          <span>
                            {formatDistanceToNow(parseISO(entry.changed_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
        </Dialog>
      )}
    </div>
  );
}